import { useState, useEffect } from 'react';
import { ref as dbRef, onValue, get } from 'firebase/database';
import { database } from '../config/firebase';

/**
 * useCars — Fase 3 del piano (refactor hook multi-tenant).
 *
 * CAMBIAMENTO: legge da companies/{companyId}/cars invece che da 'cars' root-level.
 * Se companyId è null/undefined, non avvia nessuna sottoscrizione e restituisce
 * un array vuoto — questo protegge contro letture accidentali prima che il
 * CompanyContext abbia risolto il companyId dell'utente loggato.
 *
 * L'interfaccia pubblica dell'hook (cars, loading, error) resta invariata,
 * quindi tutti i componenti che lo usano non richiedono modifiche.
 *
 * OTTIMIZZAZIONE COSTI (luglio 2026): aggiunta l'opzione `realtime`.
 * Di default resta `true` (comportamento invariato, listener live — utile in
 * pannelli admin dove serve vedere subito le modifiche). Le pagine pubbliche
 * ad alto traffico (catalogo auto per i visitatori) possono passare
 * `{ realtime: false }` per fare un fetch one-time (`get`) invece di aprire
 * un listener persistente per ogni visitatore — riduce le connessioni aperte
 * e la banda scaricata su un dato che comunque non richiede aggiornamento
 * istantaneo lato cliente.
 */
export const useCars = (companyId, options = {}) => {
  const { realtime = true } = options;
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setCars([]);
      setLoading(false);
      return;
    }

    const carsRef = dbRef(database, `companies/${companyId}/cars`);

    const applySnapshot = (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const carsArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setCars(carsArray);
      } else {
        setCars([]);
      }
      setLoading(false);
    };

    if (!realtime) {
      let cancelled = false;
      get(carsRef)
        .then((snapshot) => {
          if (!cancelled) applySnapshot(snapshot);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('Errore nel caricamento auto:', err);
          setError(err);
          setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const unsubscribe = onValue(
      carsRef,
      applySnapshot,
      (err) => {
        console.error('Errore nel caricamento auto:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, realtime]);

  return { cars, loading, error };
};
